import { Inject } from '@odinjs/odin';
import { FieldAPI, FormAPI, InputAPI, PanelAPI, TextAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnBeforeCancel, OnChange, OnClose, OnMount, OnSubmit } from '@philips-internal/oswald-ui-core/lib/client';
import { AbortableCloseEvent, ChangeEvent, MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpResponse, HttpService, MessageService, } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { generateUUID } from '@philips-internal/oswald-ui-utils';
import moment from 'moment';
import { FragmentNames, MedicationScheduleAliases } from '../../../../codegen';
import { ISO_FULL_DATE_FORMAT } from '../../../../constants/date-utils';
import { Endpoint } from '../../../../constants/endpoints';
import { ApplicationRoute } from '../../../../constants/routes';
import { FoundationUtils } from '../../../../utils/foundation-utils';
import { TimeUtils } from '../../../../utils/time-utils';
import BaseController from '../../../BaseController';
import InformationBarUtils from '../../../commons/header/information-bar-header-utils';
import { MedicineAndSolutionsParams } from '../medicine-and-solutions/medicine-and-solutions-params';
import './schedule-medications-and-solutions-controller.css';
import TimelineContext, { PrescriptionType } from '../../services/timeline-service';

interface ResponseBody {
  consistencyDescription: string;
}

interface ErrorDescription {
  errorDescription: string;
}

interface StepOrStart {
  stepSolutionStatus: string;
}

interface CurrentStepSolution {
  currentStepSolution: string;
}

interface SaveSchedulePrescriptionResponse {
  inconsistencyType: string,
  inconsistentDescription: string
}

interface ReasonResponse {
  reasonDescription: string,
  sequenceNumber: number
}

enum Status {
  ALLOWED = 'S',
  NOT_ALLOWED = 'N'
}

enum ScheduleType {
  MEDICATIONS = 'Medications',
  SOLUTIONS = 'Solutions',
  BLADDER_IRRIGATION = 'BladderIrrigation'
}
@Controller({ component: MedicationScheduleAliases.MEDICATION_SCHEDULE_LAYOUT, fragment: FragmentNames.MEDICATION_SCHEDULE })
class ScheduleMedicationsAndSolutionsController extends BaseController {

  @Inject(MedicationScheduleAliases.MEDICATION_SCHEDULE_CONTENT_FORM)
  private formMedicationScheduleApi!: FormAPI;

  @Inject(MedicationScheduleAliases.MEDICATION_SCHEDULE_CONTENT_TEXT)
  private medicationScheduleContentText!: TextAPI;

  @Inject(MedicationScheduleAliases.MEDICATION_SCHEDULE_TOAST_NOTIFICATION_PANEL)
  private medicationScheduleToastNotificationPanel!: PanelAPI;

  @Inject(MedicationScheduleAliases.TOAST_NOTIFICATION_PANEL_ALERT)
  private toastNotificationPanelalert!: PanelAPI;

  @Inject(MedicationScheduleAliases.MEDICATION_SCHEDULE_CONTENT_TEXT_AREA_FIELD)
  private fieldTextArea!: FieldAPI;

  medicineAndSolutions = MedicineAndSolutionsParams.params;

  state = {
    userName: foundationContext.getUser()?.username
  } as Record<string, Value>

  private mountBodySaveSchedule(reasonSequence: number, justificationDescription: string, establishmentId?: string,
    itemQuantity?: number, itemSequence?: number, prescriptionNumber?: number, prescriptionsNumber?: number): Record<string, Value> {
    const dateTime = this.context.get('scheduleDate') as moment.Moment;
    const cpoeSequence = this.medicineAndSolutions?.cpoeSequence;
    const encounterNumber = Number(this.medicineAndSolutions?.encounterId);
    const groupingNumber = Number(this.medicineAndSolutions?.nrGroupingId);
    const intervalCode = String(this.medicineAndSolutions?.cdIntervalId);
    const itemCode = String(this.medicineAndSolutions?.cdMaterialId);
    const unitMeasurementCode = this.medicineAndSolutions?.uom;
    const viaApplicationType = this.medicineAndSolutions?.ieViaApplicationId;
    const itemType = this.getItemType();
    const prescriptionQuantityHour = 24;
    const internalProcedureSequence = this.medicineAndSolutions?.internalProcedureSequence;
    const acmSnStatus = this.medicineAndSolutions?.ieAcmId;

    return {
      acmSnStatus: acmSnStatus,
      calledBloodGlucoseStatus: Status.NOT_ALLOWED,
      componentsDescription: null,
      cpoeSequence,
      encounterNumber,
      establishmentCode: establishmentId,
      generateEventType: null,
      groupingNumber,
      hourSequence: null,
      inconsistencyType: null,
      inconsistentDescription: null,
      intervalCode,
      itemCode,
      itemQuantity,
      itemSequence,
      itemType: itemType,
      justificationDescription,
      laboratoryStatus: Status.NOT_ALLOWED,
      objectSequence: null,
      prescriptionNumber,
      prescriptionQuantityHour: prescriptionQuantityHour,
      prescriptionsNumber: prescriptionsNumber ?? prescriptionNumber,
      procInternalSequence: internalProcedureSequence,
      protBloodGlucoseNumber: null,
      questionSequence: null,
      reasonSequence,
      scheduleIntervStatus: Status.NOT_ALLOWED,
      signatureSequence: null,
      timeHour: dateTime?.format(ISO_FULL_DATE_FORMAT),
      unitMeasurementCode: unitMeasurementCode ?? null,
      userName: String(this.state.userName),
      viaApplicationType
    }
  }

  private getItemType(): PrescriptionType | string {
    const itemTypeContext = TimelineContext.get().itemType;
    const fallbackItemType = this.medicineAndSolutions?.ieTypeItem || '';
    return this.isSolutionOrMedicineType(itemTypeContext)
      ? fallbackItemType
      : itemTypeContext;
  }

  private isSolutionOrMedicineType(itemTypeContext: string): boolean {
    return [PrescriptionType.SOLUTION, PrescriptionType.MEDICINE].includes(itemTypeContext as PrescriptionType);
  }

  private setSaveSchedulePrescription(body: Record<string, Value>): void {
    this.formMedicationScheduleApi.setLoading(true);
    HttpService.post(Endpoint.ADEP_SAVE_SCHEDULE_PRESCRIPTION, {}, { body }).then(response => {
      const saveResponseBody = response.body as SaveSchedulePrescriptionResponse;
      if (saveResponseBody?.inconsistencyType === 'P') {
        this.showToast('tws.schedule-medication-infusion.no-prescription-in-datehour-error', ToastLevel.WARNING);
        this.formMedicationScheduleApi.setDisabled(false);
        this.formMedicationScheduleApi.setLoading(false);
      } else {
        this.showToast('text.item-scheduled-successfully', ToastLevel.SUCCESS);
        applicationContext.popNavigationItem();
      }
    });
  }
  saveSchedulePrescription(): void {
    const reasonValue = this.context.get('autoCompleteValue');
    const justificationValue = this.context.get('textAreaValue');
    const establishment = foundationContext.getEstablishment();
    const itemQuantity = Number(this.medicineAndSolutions?.qtDoseId) ?? 1;
    const itemSequence = Number(this.medicineAndSolutions?.nrSequenceId);
    const prescriptionNumber = Number(this.medicineAndSolutions?.nrPrescriptionId);
    const prescriptionsNumber = Number(this.medicineAndSolutions?.nrPrescriptionsId);

    const body = this.mountBodySaveSchedule(reasonValue, justificationValue, establishment?.id, itemQuantity, itemSequence, prescriptionNumber, prescriptionsNumber)
    this.setSaveSchedulePrescription(body);
  }

  showToast(messge: string, typeMessage: ToastLevel): void {
    ToastService.open({
      dismissExpression: MessageService.translate('tws-ok.ok-text'),
      dismissible: true,
      highlight: false,
      id: generateUUID(),
      level: typeMessage,
      message: messge,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    });
  }

  verifyReturnOfProcAndRedirect(returnProcOfValidete: string | undefined): void {
    const resultParameter = FoundationUtils.findModuleParam('ADEP#1113', 'ALLOW_ACCESS_ADEP#51');

    if (!returnProcOfValidete) {
      this.saveSchedulePrescription();
    } else if (returnProcOfValidete && resultParameter === 'C') {
      this.showToast(String(returnProcOfValidete), ToastLevel.WARNING);
    } else if (returnProcOfValidete && resultParameter === 'I') {
      this.showToast(String(returnProcOfValidete), ToastLevel.ERROR);
      applicationContext.popNavigationItem();
    } else if (returnProcOfValidete && resultParameter === 'N' || returnProcOfValidete && resultParameter === 'A') {
      this.saveSchedulePrescription();
    }
  }

  confirmationScheduleMedications(): Promise<HttpResponse<ResponseBody>> {
    const dateTime = this.context.get('scheduleDate') as moment.Moment;
    const establishment = foundationContext.getEstablishment();

    const body: Record<string, Value> = {
      acmSnStatus: this.medicineAndSolutions?.ieAcmId,
      cpoeSequence: this.medicineAndSolutions?.cpoeSequence,
      encounterNumber: this.medicineAndSolutions?.encounterId,
      establishmentCode: establishment?.id,
      intervalCode: this.medicineAndSolutions?.cdIntervalId,
      itemCode: this.medicineAndSolutions?.cdMaterialId,
      itemQuantity: this.medicineAndSolutions?.qtDoseId,
      itemSequence: this.medicineAndSolutions?.nrSequenceId,
      itemType: this.medicineAndSolutions?.ieTypeItem,
      prescriptionNumber: this.medicineAndSolutions?.nrPrescriptionId,
      prescriptionsNumber: this.medicineAndSolutions?.nrPrescriptionsId,
      timeDate: dateTime?.format(ISO_FULL_DATE_FORMAT),
      unitMeasurementCode: this.medicineAndSolutions?.uom ?? null,
      userName: String(this.state.userName),
      viaApplicationType: this.medicineAndSolutions?.ieViaApplicationId,
    }

    return HttpService.post(Endpoint.ADEP_VERIFY_SCHEDULE_MEDICATIONS, {}, { body })
      .then((response) => {
        return response as HttpResponse<ResponseBody>;
      });
  }

  getCurrentStepSolution(): Promise<CurrentStepSolution> {
    const prescriptionNumber = this.medicineAndSolutions?.nrPrescriptionId;
    const solutionSequence = this.medicineAndSolutions?.nrSequenceId;

    return HttpService.get(Endpoint.ADEP_VERIFY_CURRENT_STEP_SOLUTION, {
      prescriptionNumber, solutionSequence
    }).then((response) => {
      return response.body as CurrentStepSolution;
    });
  }

  scheduleStepSolution(currentStepSolution: number): void {
    const dateTime = this.context.get('scheduleDate') as moment.Moment;
    const reasonValue = this.context.get('autoCompleteValue');
    const justificationValue = this.context.get('textAreaValue');
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();
    const redirectRoute = ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION;

    const body: Record<string, Value> = {
      gedipaType: Status.NOT_ALLOWED,
      generateKitSolAcmType: Status.NOT_ALLOWED,
      justificationDescription: String(justificationValue),
      onlyGedipaType: Status.NOT_ALLOWED,
      originFunctionCode: 9129,
      prescriptionNumber: this.medicineAndSolutions?.nrPrescriptionId,
      reasonSequence: Number(reasonValue),
      signatureSequence: 0,
      solutionSequence: this.medicineAndSolutions?.nrSequenceId,
      solutionType: 1,
      startDate: dateTime?.format(ISO_FULL_DATE_FORMAT),
      stepSequence: currentStepSolution + 1,
      userName: this.state.userName,
    }

    HttpService.post(Endpoint.ADEP_SCHEDULE_STEP_SOLUTION, {}, { body })
      .then(() => {
        this.showToast('text.item-scheduled-successfully', ToastLevel.SUCCESS);
        applicationContext.redirect(redirectRoute, { encounterId, patientId });
        applicationContext.clearNavigationStack();
      })
      .catch((err) => {
        if (err.body?.errors.length > 0) {
          const message = err.body?.errors[0].replace(/#*/, '');
          this.showToast(String(message), ToastLevel.ERROR);
          applicationContext.redirect(redirectRoute, { encounterId, patientId });
        }
      });
  }

  consistStepScheduleSolution(): void {
    const dateTime = this.context.get('scheduleDate') as moment.Moment;
    const reasonValue = this.context.get('autoCompleteValue');
    const justificationValue = this.context.get('textAreaValue');

    const body: Record<string, Value> = {
      errorDescription: null,
      gedipaType: Status.NOT_ALLOWED,
      generateKitSolAcmType: Status.NOT_ALLOWED,
      justificationDescription: String(justificationValue),
      onlyGedipaType: Status.NOT_ALLOWED,
      originFunctionCode: 9129,
      prescriptionNumber: this.medicineAndSolutions?.nrPrescriptionId,
      reasonSequence: Number(reasonValue),
      solutionSequence: this.medicineAndSolutions?.nrSequenceId,
      solutionType: 1,
      startDate: dateTime?.format(ISO_FULL_DATE_FORMAT),
      stepSequence: this.medicineAndSolutions?.nrCurrentStageId,
      userName: this.state.userName,
    }

    HttpService.post(Endpoint.ADEP_CONSIST_STEP_SOLUTION, {}, { body })
      .then((res): void => {
        const response = res.body as HttpResponse<ErrorDescription>;
        if (response.body?.errorDescription) {
          this.verifyReturnOfProcAndRedirect(response.body?.errorDescription);
          return;
        }
        this.getCurrentStepSolution().then(res2 => {
          this.scheduleStepSolution(Number(res2?.currentStepSolution));
        })
      });

  }

  startScheduleSolution(): void {
    const dateTime = this.context.get('scheduleDate') as moment.Moment;
    const reasonValue = this.context.get('autoCompleteValue');
    const justificationValue = this.context.get('textAreaValue');
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();
    const redirectRoute = ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION;

    const body: Record<string, Value> = {
      gedipaType: Status.NOT_ALLOWED,
      generateKitSolAcmType: Status.NOT_ALLOWED,
      justificationDescription: String(justificationValue),
      onlyGedipaType: Status.NOT_ALLOWED,
      originFunctionCode: 9129,
      prescriptionNumber: this.medicineAndSolutions?.nrPrescriptionId,
      reasonSequence: Number(reasonValue),
      signatureSequence: 0,
      solutionSequence: this.medicineAndSolutions?.nrSequenceId,
      solutionType: 1,
      startDate: dateTime?.format(ISO_FULL_DATE_FORMAT),
      userName: this.state.userName,
    }

    HttpService.post(Endpoint.ADEP_START_SCHEDULE_SOLUTION, {}, { body })
      .then(() => {
        this.showToast('text.item-scheduled-successfully', ToastLevel.SUCCESS);
        applicationContext.redirect(redirectRoute, {
          encounterId,
          patientId,
        });
        applicationContext.clearNavigationStack();
      })
      .catch((err) => {
        const message = err.body.errors[0];
        this.showToast(String(message), ToastLevel.ERROR);
        applicationContext.redirect(redirectRoute, {
          encounterId,
          patientId,
        });
      });
  }

  verifyIfStepOrStart(): Promise<HttpResponse<StepOrStart>> {
    const { nrPrescriptionId, nrSequenceId } = MedicineAndSolutionsParams.params;

    return HttpService.get(Endpoint.ADEP_VERIFY_STEP_OR_START, {
      prescriptionNumber: nrPrescriptionId,
      solutionSequence: nrSequenceId,
    });
  }

  private verifyIfMedOrSolution(scheduleTypeId: string): void {
    const validScheduleTypes = [String(ScheduleType.MEDICATIONS), ScheduleType.BLADDER_IRRIGATION];
    if (validScheduleTypes.includes(scheduleTypeId)) {
      this.confirmationScheduleMedications().then(({ body }) => {
        this.verifyReturnOfProcAndRedirect(body?.consistencyDescription);
      });
      return;
    }

    if (scheduleTypeId === ScheduleType.SOLUTIONS) {
      this.verifyIfStepOrStart().then(response => {
        if (response.body?.stepSolutionStatus === Status.NOT_ALLOWED) {
          this.startScheduleSolution();
          return;
        }
        this.consistStepScheduleSolution();
      });
    }
  }
  onclickMedicationScheduleButton(): void {
    this.formMedicationScheduleApi.setDisabled(true);
    const { scheduleTypeId } = this.historyService.getCurrentRouteParams();
    const textArea = this.fieldTextArea.getData();
    const autoComplete = this.context.get('autoCompleteValue');

    if (!textArea || !autoComplete) {
      this.medicationScheduleToastNotificationPanel.setVisible(true);
      return;
    }
    this.verifyIfMedOrSolution(scheduleTypeId as string);
  }

  @OnMount({ target: MedicationScheduleAliases.MEDICATION_SCHEDULE_LAYOUT })
  handleMountScheduleLayout(): void {
    InformationBarUtils.collapsedBar = true;
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();

    this.context.set({ encounterId, patientId });
  }

  @OnMount({ target: MedicationScheduleAliases.MEDICATION_SCHEDULE_CONTENT_TEXT })
  handleMountScheduleText(): void {
    const scheduleMedicationParams = MedicineAndSolutionsParams.params?.dsMaterialId;
    this.medicationScheduleContentText.setValue(String(scheduleMedicationParams));
  }

  @OnMount({ target: MedicationScheduleAliases.MEDICATION_SCHEDULE_CONTENT_FORM })
  handleMountScheduleForm(): void {
    const scheduleDate = moment();
    this.formMedicationScheduleApi.setData({ dateFeld: scheduleDate.format('L LTS') });
    const establishment = foundationContext.getEstablishment()?.id;
    this.context.set({ establishment, scheduleDate });
  }

  @OnMount({ target: MedicationScheduleAliases.MEDICATION_SCHEDULE_INPUT_DATE_TIME_FIELD })
  handleMountScheduleDateTime(event: MountEvent): void {
    TimeUtils.setDateToInput(event.target as InputAPI)
      .then(date => this.context.set({ scheduleDate: moment(date) }))
  }

  @OnChange({ target: MedicationScheduleAliases.MEDICATION_SCHEDULE_INPUT_DATE_TIME_FIELD })
  handleChangeScheduleDateTime(evt: ChangeEvent<FieldAPI>): void {
    const updatedScheduleDate = moment(String(evt.value));
    this.context.set({ scheduleDate: updatedScheduleDate });
  }

  @OnChange({ target: MedicationScheduleAliases.MEDICATION_SCHEDULE_CONTENT_AUTOCOMPLETE })
  handleChangeScheduleAutoComplete(event: ChangeEvent<FieldAPI>): void {
    const reason = event.value as unknown as ReasonResponse;
    this.context.set({
      autoCompleteValue: reason.sequenceNumber,
    });
  }

  @OnChange({ target: MedicationScheduleAliases.MEDICATION_SCHEDULE_CONTENT_TEXT_AREA })
  handleChangeScheduleTextArea(event: ChangeEvent<FieldAPI>): void {
    this.context.set({
      textAreaValue: event.value,
    });
  }

  @OnClose({ target: MedicationScheduleAliases.MEDICATION_SCHEDULE_TOAST_NOTIFICATION })
  handleCloseScheduleToastNotification(): void {
    this.medicationScheduleToastNotificationPanel.setVisible(false);
  }

  @OnClose({ target: MedicationScheduleAliases.TOAST_NOTIFICATION_ALERT })
  handleCloseToastNotificationAlert(): void {
    this.toastNotificationPanelalert.setVisible(false);
  }

  @OnBeforeCancel({ target: MedicationScheduleAliases.MEDICATION_SCHEDULE_CONTENT_FORM })
  handleBeforeCancelScheduleForm(event: AbortableCloseEvent): void {
    event.abort();
    applicationContext.popNavigationItem();
  }

  @OnSubmit({ target: MedicationScheduleAliases.MEDICATION_SCHEDULE_CONTENT_FORM })
  handleSubmitScheduleForm(): void {
    this.onclickMedicationScheduleButton();
  }
}

export default ScheduleMedicationsAndSolutionsController;
